import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ROUTES } from "../../constants/routes";
import Header from "../../components/header/Header";
// @ts-ignore vendored plain-JS engine (gnovotny/nothing-to-watch)
import voroforce from "../../vendor/voroforce";
// @ts-ignore
import { isTouchDevice } from "../../vendor/voroforce/utils/device";
import config, { CELLS, MEDIA_BASE_URL, SUBGRID_SIZE } from "./vf/config";
import { localizedPoster, localizedTitle, mediaLocale, pickCellIdOffset } from "./vf/slice";

const JSON_BASE_URL = process.env.REACT_APP_VORO_JSON_URL || "/voro/json";
const DATA_BASE_URL = JSON_BASE_URL.replace(/\/json$/, "");
const POSTER_BASE_URL = "https://image.tmdb.org/t/p/w185";

type Movie = {
  id: number;
  title: string;
  title_ru?: string;
  title_ua?: string;
  title_ar?: string;
  poster_path?: string;
  poster_path_ru?: string;
  poster_path_ua?: string;
  poster_path_ar?: string;
  release_date?: string;
  vote_average?: number;
};

type Cell = { id: number; subgrid: number; subgridIndex: number };

const batches = new Map<number, Movie[]>();

// The visit window wraps around the pool, so a cell's subgrid can run past the last batch —
// the same modulo the shader applies to the atlas layer (id / cap % layers).
const getCellMovie = async (cell: Cell, batchCount: number): Promise<Movie | undefined> => {
  const index = batchCount > 0 ? cell.subgrid % batchCount : cell.subgrid;
  let batch = batches.get(index);
  if (!batch) {
    try {
      const res = await fetch(`${JSON_BASE_URL}/${index}.json`);
      batch = res.ok ? await res.json() : [];
    } catch {
      batch = [];
    }
    batches.set(index, batch as Movie[]);
  }
  return batch?.[cell.subgridIndex % SUBGRID_SIZE];
};

const ExplorePage = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [movie, setMovie] = useState<Movie>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let instance: any;
    let disposed = false;
    // ponytail: flat halving on touch. Upstream picks cell counts per GPU class with
    // detect-gpu; swap that in if phones still struggle.
    const cells = isTouchDevice ? Math.round(CELLS / 2) : CELLS;

    const start = async () => {
      // The baked pool is bigger than the number of cells on screen, so start the cell ids
      // at a random batch boundary: every visit shows a different slice of the catalogue.
      let cellIdOffset = 0;
      let batchCount = 0;
      let poolCount = 0;
      try {
        const res = await fetch(`${DATA_BASE_URL}/manifest.json`);
        if (!res.ok) throw new Error(`${res.status}`);
        const { count, subgridSize = SUBGRID_SIZE } = await res.json();
        poolCount = count;
        batchCount = Math.floor(count / subgridSize);
        cellIdOffset = pickCellIdOffset(count, subgridSize);
      } catch (e) {
        console.error("voro manifest unavailable", e); // no data at all -> the probe below reports it
        setFailed(true);
        return;
      }
      if (disposed) return;

      // Needs WebGL2 as well as the atlases; either can be missing in a deployed
      // environment, so fail visibly instead of showing black.
      try {
        instance = voroforce(container, {
          ...config,
          cells,
          // Artwork is baked per locale, and layer counts have to match what was baked
          // exactly: the shader wraps the layer index, so a count the bake didn't produce
          // would either 404 or show a tile the metadata doesn't agree with.
          media: {
            ...config.media,
            baseUrl: `${MEDIA_BASE_URL}/${mediaLocale(i18n.language)}`,
            versions: config.media.versions.map((version) => ({
              ...version,
              layers: Math.ceil(poolCount / (version.cols * version.rows)),
            })),
          },
          lattice: { ...config.lattice, cellIdOffset },
        });
      } catch (e) {
        console.error("voroforce init failed", e);
        setFailed(true);
        return;
      }

      const openMovie = (selected: Movie) => navigate(`${ROUTES.DETAILS}/${selected.id}`);
      let tappedId: number | undefined;

      instance.controls.listen("focused", ({ cell }: { cell?: Cell }) => {
        if (cell) void getCellMovie(cell, batchCount).then(setMovie);
      });
      instance.controls.listen("selected", ({ cell }: { cell?: Cell }) => {
        if (!cell) return;
        void getCellMovie(cell, batchCount).then((selected) => {
          if (!selected) return;
          setMovie(selected);
          // On touch there is no hover: the first tap only brings a cell up, a second tap
          // on the same cell opens it. Keeps a stray tap from yanking you off the page.
          if (isTouchDevice && tappedId !== cell.id) {
            tappedId = cell.id;
            return;
          }
          openMovie(selected);
        });
      });
    };

    void start();

    void fetch(`${MEDIA_BASE_URL}/${mediaLocale(i18n.language)}/low/dds/0.dds`, { method: "HEAD" })
      .then((res) => setFailed(!res.ok))
      .catch(() => setFailed(true));

    return () => {
      disposed = true;
      // ponytail: engine dispose() touches helpers that lack a dispose method upstream;
      // swallow so a failed teardown can't blank the app on route change.
      try {
        instance?.dispose();
      } catch (e) {
        console.warn("voroforce dispose failed", e);
      }
      container.replaceChildren();
    };
  }, [navigate, i18n.language]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        // the engine sets touch-action on its container; this stops the page itself from
        // pull-to-refreshing or swiping back underneath the gallery
        touchAction: "none",
        overscrollBehavior: "none",
      }}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      <Header />
      {failed && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            opacity: 0.6,
            textAlign: "center",
            padding: 24,
          }}
        >
          Gallery unavailable — needs WebGL2 and the generated poster atlases.
        </div>
      )}
      {movie && (
        <div
          style={{
            position: "absolute",
            bottom: 24,
            left: 24,
            display: "flex",
            gap: 12,
            alignItems: "flex-end",
            color: "#fff",
            pointerEvents: "none",
            textShadow: "0 1px 4px rgba(0,0,0,.8)",
          }}
        >
          {localizedPoster(movie, i18n.language) && (
            <img
              src={`${POSTER_BASE_URL}${localizedPoster(movie, i18n.language)}`}
              alt=""
              width={60}
              style={{ borderRadius: 4 }}
            />
          )}
          <div>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{localizedTitle(movie, i18n.language)}</div>
            <div style={{ opacity: 0.7, fontSize: 14 }}>
              {movie.release_date?.slice(0, 4)}
              {movie.vote_average ? ` · ${movie.vote_average.toFixed(1)}` : ""}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExplorePage;
